/**
 * EmergencyTable — real-time emergency alerts table.
 * Source: activeAlarms from NotificationContext.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { durationToNow, durationBetween } from '../../../../libs.jsx';
import AlertStatusBadge from './AlertStatusBadge.jsx';
import AlertTypeBadge from './AlertTypeBadge.jsx';
import { formatUtcForCampus } from '../../../../utils/campusTime.js';
import { getCampusTzOffset } from '../../../../services/session.js';
import SortableTh from '../../../../components/v2/SortableTh.jsx';
import InfoPopover from '../../../../components/v2/InfoPopover.jsx';

/** Hook that ticks every second to force duration re-renders */
function useTick(intervalMs = 1000) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);
}

const OVERDUE_MS = 5 * 60 * 1000;
const CRITICAL_MS = 10 * 60 * 1000;

const getDurationColor = (status, eventStart) => {
    if (status !== 'Overdue' || !eventStart) return undefined;
    const elapsed = Date.now() - new Date(eventStart.endsWith?.('Z') ? eventStart : eventStart + 'Z').getTime();
    if (elapsed > CRITICAL_MS) return 'var(--color-danger, #e63757)';
    if (elapsed > OVERDUE_MS) return 'var(--color-warning-text, #d97706)';
    return undefined;
};

/** Duration cell — green when cleared, yellow/red when overdue; hover, focus or tap shows the response time */
const DurationCell = ({ eventStart, ackTime, status }) => {
    const hasAck = Boolean(ackTime);

    const color = hasAck
        ? 'var(--color-success, #0cce6b)'
        : status === 'Accepted'
            ? 'var(--color-primary, #0081C7)'
            : getDurationColor(status, eventStart);

    const duration = (
        <span className={`na-duration${hasAck ? ' na-duration--has-popover' : ''}`} style={color ? { color } : undefined}>
            {durationToNow(eventStart)}
        </span>
    );
    if (!hasAck) return duration;

    return (
        <InfoPopover marker="none" heading="Response Time" body={durationBetween(eventStart, ackTime)} maxWidth={260}>
            {duration}
        </InfoPopover>
    );
};

const EmergencyTable = ({ data, sortColumn, sortOrder, onSort, tzOffset = getCampusTzOffset(), highPriorityAlertTypes = new Set() }) => {
    useTick(1000); // Re-render every second for live duration updates

    const sortIcon = (col) => {
        if (sortColumn !== col) return null;
        return <span style={{ marginLeft: 4, fontSize: 10 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>;
    };

    const renderResidents = (row) => {
        const residents = row.ResidentsObject;
        if (!residents || !Array.isArray(residents) || residents.length === 0) return '—';
        return residents.map((r, i) => (
            <span key={i}>
                {r.resident_id ? (
                    <Link to={`/profiles/resident/${r.resident_id}`} className="ar-link">
                        {r.firstName || r.fName || ''} {r.lastName || r.lName || ''}
                    </Link>
                ) : (`${r.firstName || r.fName || ''} ${r.lastName || r.lName || ''}`)}
                {i < residents.length - 1 ? ', ' : ''}
            </span>
        ));
    };

    return (
        <div className="dp-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="dp-table">
                <thead className="dp-thead">
                    <tr>
                        <SortableTh sortKey="EventDateTime" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Event Time{sortIcon('EventDateTime')}</SortableTh>
                        <th className="dp-th">Duration</th>
                        <SortableTh sortKey="AlertType" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Type{sortIcon('AlertType')}</SortableTh>
                        <th className="dp-th">Residents</th>
                        <th className="dp-th">Device / Serial</th>
                        <th className="dp-th">Location / Area</th>
                        <SortableTh sortKey="NearName" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Near{sortIcon('NearName')}</SortableTh>
                        <SortableTh sortKey="AcceptUsername" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Accepted{sortIcon('AcceptUsername')}</SortableTh>
                        <SortableTh sortKey="Status" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Status{sortIcon('Status')}</SortableTh>
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr className="dp-tr">
                            <td className="dp-td" colSpan={9}>
                                <div className="na-empty">
                                    <div className="na-empty__icon">✓</div>
                                    <div className="na-empty__text">No active emergency alerts</div>
                                </div>
                            </td>
                        </tr>
                    ) : null}
                    {data.map((row) => {
                        const isHighPriority = highPriorityAlertTypes.has(row.AlertType);
                        return (
                        <tr key={row.AlarmId} className={`dp-tr na-row-border${isHighPriority ? ' na-row--priority' : ''}`} style={{ borderLeftColor: isHighPriority ? 'var(--color-danger, #e63757)' : row.AlertType === 'Emergency' ? 'var(--color-danger)' : row.AlertType === 'Maintenance' ? 'var(--color-warning)' : 'var(--color-primary)' }}>
                            <td className="dp-td" style={{ whiteSpace: 'nowrap' }}>{formatUtcForCampus(row.EventDateTime, tzOffset)}</td>
                            <td className="dp-td">
                                <DurationCell eventStart={row.EventDateTime} ackTime={row.AckDateTime} status={row.Status} />
                            </td>
                            <td className="dp-td">
                                <div className="na-stack">
                                    {isHighPriority ? (
                                        <span className="na-badge na-badge--priority" title="High priority alert type">
                                            {'⚑'} High Priority
                                        </span>
                                    ) : null}
                                    <AlertTypeBadge alertType={row.AlertType} alertSubType={row.AlertSubType} />
                                </div>
                            </td>
                            <td className="dp-td">{renderResidents(row)}</td>
                            <td className="dp-td">
                                <div className="na-stack">
                                    {row.DeviceId ? (
                                        <Link to={`/profiles/inovonics/${row.DeviceId}`} className="ar-link">{row.DeviceUse || '—'}</Link>
                                    ) : (<span>{row.DeviceUse || '—'}</span>)}
                                    <span className="na-stack__sub">{row.SerialNumber || ''}</span>
                                </div>
                            </td>
                            <td className="dp-td">
                                <div className="na-stack">
                                    {row.LocationId ? (
                                        <Link to={`/profiles/area/${row.LocationId}`} className="ar-link">{row.LocationName || '—'}</Link>
                                    ) : (<span>{row.LocationName || '—'}</span>)}
                                    <span className="na-stack__sub">{row.LocationArea || ''}</span>
                                </div>
                            </td>
                            <td className="dp-td">{row.NearName || '—'}</td>
                            <td className="dp-td">{row.AcceptUsername || '—'}</td>
                            <td className="dp-td"><AlertStatusBadge status={row.Status} eventDateTime={row.EventDateTime} /></td>
                        </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default EmergencyTable;
